import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, switchMap } from 'rxjs';
import { ShareableGroupsSyncActions } from '@states/shareable-groups-sync/shareable-groups-sync.actions';
import { SharedActions } from '@states/shared/shared.action-types';
import { ShareableGroupsSyncModel } from '@models/shareable-groups-sync.model';
import { ShareableGroupsSyncService } from '../../services/shareable-groups-sync.service';
import { UtilsService } from '../../edge/utils.service';

export const startTriggerPersonSync$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.startTriggerPersonSync),
      exhaustMap(() => [
        ShareableGroupsSyncActions.setTriggering({
          domain: ShareableGroupsSyncModel.SyncDomain.Person,
          isTriggering: true,
        }),
        ShareableGroupsSyncActions.triggerPersonSync(),
      ]),
    ),
  { functional: true },
);

export const triggerPersonSync$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShareableGroupsSyncService),
    translate = inject(TranslateService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.triggerPersonSync),
      exhaustMap(() =>
        service.triggerPersonSync().pipe(
          switchMap(() => [
            ShareableGroupsSyncActions.triggerPersonSyncSuccess(),
            ShareableGroupsSyncActions.setTriggering({
              domain: ShareableGroupsSyncModel.SyncDomain.Person,
              isTriggering: false,
            }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.SHAREABLE_GROUPS_PERSON_SYNC_TRIGGERED'),
            }),
            ShareableGroupsSyncActions.refreshData(),
          ]),
          catchError((response) => [
            ShareableGroupsSyncActions.triggerPersonSyncFail(),
            ShareableGroupsSyncActions.setTriggering({
              domain: ShareableGroupsSyncModel.SyncDomain.Person,
              isTriggering: false,
            }),
            SharedActions.showMessage({ error: utils.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startTriggerVehicleSync$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.startTriggerVehicleSync),
      exhaustMap(() => [
        ShareableGroupsSyncActions.setTriggering({
          domain: ShareableGroupsSyncModel.SyncDomain.Vehicle,
          isTriggering: true,
        }),
        ShareableGroupsSyncActions.triggerVehicleSync(),
      ]),
    ),
  { functional: true },
);

export const triggerVehicleSync$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShareableGroupsSyncService),
    translate = inject(TranslateService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.triggerVehicleSync),
      exhaustMap(() =>
        service.triggerVehicleSync().pipe(
          switchMap(() => [
            ShareableGroupsSyncActions.triggerVehicleSyncSuccess(),
            ShareableGroupsSyncActions.setTriggering({
              domain: ShareableGroupsSyncModel.SyncDomain.Vehicle,
              isTriggering: false,
            }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.SHAREABLE_GROUPS_VEHICLE_SYNC_TRIGGERED'),
            }),
            ShareableGroupsSyncActions.refreshData(),
          ]),
          catchError((response) => [
            ShareableGroupsSyncActions.triggerVehicleSyncFail(),
            ShareableGroupsSyncActions.setTriggering({
              domain: ShareableGroupsSyncModel.SyncDomain.Vehicle,
              isTriggering: false,
            }),
            SharedActions.showMessage({ error: utils.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startLoadingSyncLogs$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.startLoadingSyncLogs),
      exhaustMap(() => [ShareableGroupsSyncActions.loadSyncLogs()]),
    ),
  { functional: true },
);

export const loadSyncLogs$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShareableGroupsSyncService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.loadSyncLogs),
      switchMap(() =>
        service.getSyncLogs(ShareableGroupsSyncModel.DEFAULT_LOG_LIMIT).pipe(
          switchMap((logs) => [ShareableGroupsSyncActions.loadSyncLogsSuccess({ logs })]),
          catchError((response) => [
            ShareableGroupsSyncActions.loadSyncLogsFail(),
            SharedActions.showMessage({ error: utils.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startLoadingSyncStats$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.startLoadingSyncStats),
      exhaustMap(() => [ShareableGroupsSyncActions.loadSyncStats()]),
    ),
  { functional: true },
);

export const loadSyncStats$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShareableGroupsSyncService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.loadSyncStats),
      switchMap(() =>
        service.getSyncStats().pipe(
          switchMap((stats) => [ShareableGroupsSyncActions.loadSyncStatsSuccess({ stats })]),
          catchError((response) => [
            ShareableGroupsSyncActions.loadSyncStatsFail(),
            SharedActions.showMessage({ error: utils.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const refreshShareableGroupsSyncData$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ShareableGroupsSyncActions.refreshData),
      exhaustMap(() => [
        ShareableGroupsSyncActions.startLoadingSyncLogs(),
        ShareableGroupsSyncActions.startLoadingSyncStats(),
      ]),
    ),
  { functional: true },
);
